import React from 'react';
import CustomTextField from 'components/Fileds/CustomTextFiled';
import CustomSelect from 'components/Fileds/CustomSelect';
import CustomTypography from 'components/Fileds/CustomTypography';
import * as Yup from 'yup';
import GenericDialog from '../GenericDialog/GenericDialog';
import { useTheme, Grid } from '@mui/material';

const ItemSchema = Yup.object().shape({
  name: Yup.string().required('Item Name is required'),
  category_id: Yup.number().required('Category is required'),
  unit_type: Yup.string().required('Unit Type is required'),
  stock: Yup.number()
    .typeError('Stock must be a number')
    .min(0, 'Stock cannot be negative')
    .required('Stock is required'),
  price: Yup.number()
    .typeError('Price must be a number')
    .min(0, 'Price cannot be negative')
    .nullable(),
  batch_number: Yup.string().nullable(),
  expiry_date: Yup.date()
    .typeError('Invalid date format')
    .nullable(),
  low_stock_val: Yup.number()
    .typeError('Low stock value must be a number')
    .min(0, 'Low stock value cannot be negative')
    .required('Low stock value is required')
});

const InventoryItemsModal = ({ open, onClose, initialData, onSubmit, loading, categories = [] }) => {
  const isEdit = Boolean(initialData);
  const title = isEdit ? 'Edit Inventory Item' : 'Add New Item';
  const theme = useTheme();

  const categoryOptions = categories.map(cat => ({
    value: cat.id,
    label: cat.name
  }));

  const unitOptions = ["Kg", "gram", "Liter", "Ml", "Piece", "Dozen", "Box"].map(u => ({
    value: u,
    label: u
  }));

  const handleStockChange = (e, setFieldValue) => {
    const val = e.target.value;
    setFieldValue('stock', val);

    // Auto-calculate low_stock_val only in ADD mode
    if (!isEdit) {
      const numVal = parseFloat(val);
      if (!isNaN(numVal) && numVal >= 0) {
        setFieldValue('low_stock_val', Math.round(numVal * 0.2 * 100) / 100);
      } else {
        setFieldValue('low_stock_val', 0);
      }
    }
  };

  return (
    <GenericDialog
      open={open}
      onClose={onClose}
      title={title}
      maxWidth="md"
      initialValues={{
        name: initialData?.name || '',
        category_id: initialData?.category_id || '',
        unit_type: initialData?.unit_type || '',
        stock: initialData?.stock || 0,
        price: initialData?.price !== null && initialData?.price !== undefined ? initialData.price : '',
        batch_number: initialData?.batch_number || '',
        expiry_date: initialData?.expiry_date ? new Date(initialData.expiry_date).toISOString().split('T')[0] : '',
        low_stock_val: initialData?.low_stock_val !== null && initialData?.low_stock_val !== undefined ? initialData.low_stock_val : 0
      }}
      validationSchema={ItemSchema}
      onSubmit={onSubmit}
      submitLabel={isEdit ? 'Update' : 'Save'}
      loading={loading}
      requiredFields={['name', 'category_id', 'unit_type', 'stock', 'low_stock_val']}
    >
      {({ values, errors, touched, handleChange, handleBlur, setFieldValue }) => (
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <CustomTypography
              label="Item Name"
              sx={{ fontSize: '14px', fontWeight: 600, color: theme.palette.typographyLabel.main }}
            />
            <CustomTextField
              name="name"
              value={values.name}
              onChange={handleChange}
              onBlur={handleBlur}
              fullWidth
              placeholder="Enter Item Name"
              error={touched.name && Boolean(errors.name)}
              helperText={touched.name && errors.name}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <CustomTypography
              label="Category"
              sx={{ fontSize: '14px', fontWeight: 600, color: theme.palette.typographyLabel.main }}
            />
            <CustomSelect
              name="category_id"
              value={values.category_id}
              onChange={handleChange}
              onBlur={handleBlur}
              options={categoryOptions}
              placeholder="Select Category"
              error={touched.category_id && Boolean(errors.category_id)}
              helperText={touched.category_id && errors.category_id}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <CustomTypography
              label="Unit Type"
              sx={{ fontSize: '14px', fontWeight: 600, color: theme.palette.typographyLabel.main, mt: 1 }}
            />
            <CustomSelect
              name="unit_type"
              value={values.unit_type}
              onChange={handleChange}
              onBlur={handleBlur}
              options={unitOptions}
              placeholder="Select Unit Type"
              error={touched.unit_type && Boolean(errors.unit_type)}
              helperText={touched.unit_type && errors.unit_type}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <CustomTypography
              label="Price"
              sx={{ fontSize: '14px', fontWeight: 600, color: theme.palette.typographyLabel.main, mt: 1 }}
            />
            <CustomTextField
              name="price"
              inputProps={{ inputMode: 'decimal' }}
              value={values.price}
              onChange={(e) => {
                const val = e.target.value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');
                setFieldValue('price', val);
              }}
              onBlur={handleBlur}
              fullWidth
              placeholder="Enter Price"
              error={touched.price && Boolean(errors.price)}
              helperText={touched.price && errors.price}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <CustomTypography
              label="Stock"
              sx={{ fontSize: '14px', fontWeight: 600, color: theme.palette.typographyLabel.main, mt: 1 }}
            />
            <CustomTextField
              name="stock"
              inputProps={{ inputMode: 'decimal' }}
              value={values.stock}
              onChange={(e) => {
                const val = e.target.value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');
                handleStockChange({ target: { value: val } }, setFieldValue);
              }}
              onBlur={handleBlur}
              fullWidth
              placeholder="Enter Initial Stock"
              error={touched.stock && Boolean(errors.stock)}
              helperText={touched.stock && errors.stock}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <CustomTypography
              label="Low Stock Value"
              sx={{ fontSize: '14px', fontWeight: 600, color: theme.palette.typographyLabel.main, mt: 1 }}
            />
            <CustomTextField
              name="low_stock_val"
              inputProps={{ inputMode: 'decimal' }}
              value={values.low_stock_val}
              onChange={(e) => {
                const val = e.target.value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');
                setFieldValue('low_stock_val', val);
              }}
              onBlur={handleBlur}
              fullWidth
              placeholder="Enter Low Stock Threshold"
              error={touched.low_stock_val && Boolean(errors.low_stock_val)}
              helperText={touched.low_stock_val && errors.low_stock_val}
            />
          </Grid>


        

          <Grid item xs={12} sm={6}>
            <CustomTypography
              label="Batch Number"
              sx={{ fontSize: '14px', fontWeight: 600, color: theme.palette.typographyLabel.main, mt: 1 }}
            />
            <CustomTextField
              name="batch_number"
              value={values.batch_number}
              onChange={handleChange}
              onBlur={handleBlur}
              fullWidth
              placeholder="Enter Batch Number"
              error={touched.batch_number && Boolean(errors.batch_number)}
              helperText={touched.batch_number && errors.batch_number}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <CustomTypography
              label="Expiry Date"
              sx={{ fontSize: '14px', fontWeight: 600, color: theme.palette.typographyLabel.main, mt: 1 }}
            />
            <CustomTextField
              name="expiry_date"
              type="date"
              value={values.expiry_date}
              onChange={handleChange}
              onBlur={handleBlur}
              fullWidth
              placeholder="Select Expiry Date"
              error={touched.expiry_date && Boolean(errors.expiry_date)}
              helperText={touched.expiry_date && errors.expiry_date}
            />
          </Grid>

        </Grid>
      )}
    </GenericDialog>
  );
};

export default InventoryItemsModal;
